import React, { useState } from 'react';
import { GradientText } from '../components/ui/GradientText';
import { MessageSquare, Code2, Briefcase, Mail, ArrowRight, Copy, Check, ExternalLink } from 'lucide-react';
import Footer from '../components/Footer';
import { FloatingWidget } from '../components/widget/FloatingWidget';

interface CodeTabsProps {
  tabs: {
    label: string;
    code: string;
  }[];
}

function CodeTabs({ tabs }: CodeTabsProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [copied, setCopied] = useState<number | null>(null);

  const copyToClipboard = (code: string, index: number) => {
    navigator.clipboard.writeText(code);
    setCopied(index);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="bg-black/50 rounded-xl border border-white/10 overflow-hidden">
      <div className="flex border-b border-white/10">
        {tabs.map((tab, index) => (
          <button
            key={index}
            onClick={() => setActiveTab(index)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === index
                ? 'text-white bg-white/5'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="relative">
        <pre className="p-4 text-sm text-gray-300 overflow-x-auto">
          <code>{tabs[activeTab].code}</code>
        </pre>
        <button
          onClick={() => copyToClipboard(tabs[activeTab].code, activeTab)}
          className="absolute top-2 right-2 p-2 rounded-lg bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
        >
          {copied === activeTab ? (
            <Check className="w-4 h-4 text-green-400" />
          ) : (
            <Copy className="w-4 h-4" />
          )}
        </button>
      </div>
    </div>
  );
}

export default function Integrations() {
  return (
    <div className="bg-black">
      {/* Hero Section */}
      <div className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(79,70,229,0.1),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(219,39,119,0.1),transparent_50%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Integrate <GradientText>Botmaker</GradientText> everywhere
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Découvrez toutes les possibilités d'intégration de notre chatbot IA et connectez-le à vos outils existants.
            </p>
          </div>
        </div>
      </div>

      {/* Types d'intégration */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">
              Types <GradientText>d'Intégration</GradientText>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Choisissez le mode d'intégration qui correspond le mieux à vos besoins.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {/* Widget Flottant */}
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
              <MessageSquare className="w-12 h-12 text-indigo-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">Widget Flottant</h3>
              <p className="text-gray-400 mb-6">Le chatbot s'affiche sous forme de bulle flottante en bas de votre site.</p>
              
              <CodeTabs tabs={[{
                label: 'Simple',
                code: `<script src="https://botmaker-agency.ai/widget.js"></script>
<script>
  initBotmaker('YOUR_BOT_ID');
</script>`
              }, {
                label: 'Options',
                code: `<script>
  window.botmakerConfig = {
    id: 'YOUR_BOT_ID',
    position: 'bottom-right',
    offset: { x: 20, y: 20 },
    theme: 'light'
  };
</script>
<script src="https://botmaker-agency.ai/widget.js"></script>`
              }]} />
            </div>

            {/* Intégration Inline */}
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
              <Code2 className="w-12 h-12 text-purple-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">Intégration Inline</h3>
              <p className="text-gray-400 mb-6">Intégrez le chatbot directement dans une section de votre page.</p>
              
              <CodeTabs tabs={[{
                label: 'Simple',
                code: `<iframe 
  src="https://botmaker-agency.ai/widget?id=YOUR_BOT_ID"
  style="width: 100%; height: 600px; border: none;"
></iframe>`
              }, {
                label: 'Responsive',
                code: `<div style="position: relative; padding-bottom: 56.25%; height: 0;">
  <iframe 
    src="https://botmaker-agency.ai/widget?id=YOUR_BOT_ID"
    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none;"
  ></iframe>
</div>`
              }]} />
            </div>

            {/* Page Dédiée */}
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
              <ExternalLink className="w-12 h-12 text-pink-400 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-3">Page Dédiée</h3>
              <p className="text-gray-400 mb-6">Créez une page dédiée pour votre chatbot avec une URL personnalisée.</p>
              
              <div className="bg-black/50 rounded-lg p-4 text-gray-300">
                <p className="mb-2 font-medium">URL de votre chatbot :</p>
                <code className="text-sm">
                  https://botmaker-agency.ai/widget?id=YOUR_BOT_ID
                </code>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intégrations CRM */}
      <section className="py-20 bg-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">
              Intégrations <GradientText>CRM</GradientText>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Connectez votre chatbot à vos outils CRM pour un suivi optimal de vos leads.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Salesforce */}
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-8 border border-white/10">
              <div className="flex items-start gap-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center shrink-0">
                  <Briefcase className="w-6 h-6 text-indigo-400" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-3">Salesforce</h3>
                  <p className="text-gray-300 mb-6">
                    Synchronisez automatiquement vos leads avec Salesforce. Les contacts détectés sont instantanément créés ou mis à jour dans votre CRM.
                  </p>
                  <a 
                    href="/app/settings/integrations/crm"
                    className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 transition-colors"
                  >
                    Configurer l'intégration
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>

            {/* HubSpot */}
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-8 border border-white/10">
              <div className="flex items-start gap-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center shrink-0">
                  <Briefcase className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-3">HubSpot</h3>
                  <p className="text-gray-300 mb-6">
                    Intégrez votre chatbot avec HubSpot pour un suivi complet de vos contacts et une automatisation de vos workflows marketing.
                  </p>
                  <a 
                    href="/app/settings/integrations/crm"
                    className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors"
                  >
                    Configurer l'intégration
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Notifications Email */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">
              Notifications <GradientText>Email</GradientText>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Restez informé en temps réel des interactions avec votre chatbot.
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-xl rounded-xl p-8 border border-white/10">
            <div className="flex items-start gap-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500/20 to-orange-500/20 flex items-center justify-center shrink-0">
                <Mail className="w-6 h-6 text-pink-400" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-3">Configuration des Notifications</h3>
                <p className="text-gray-300 mb-6">
                  Personnalisez vos notifications email selon vos besoins :
                </p>
                <ul className="space-y-4 mb-8">
                  <li className="flex items-center gap-3 text-gray-300">
                    <div className="w-6 h-6 rounded-lg bg-green-500/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-green-400" />
                    </div>
                    Notifications instantanées pour chaque nouveau lead
                  </li>
                  <li className="flex items-center gap-3 text-gray-300">
                    <div className="w-6 h-6 rounded-lg bg-green-500/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-green-400" />
                    </div>
                    Rapports journaliers des conversations
                  </li>
                  <li className="flex items-center gap-3 text-gray-300">
                    <div className="w-6 h-6 rounded-lg bg-green-500/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-green-400" />
                    </div>
                    Résumés hebdomadaires des performances
                  </li>
                </ul>
                <a 
                  href="/app/settings/integrations/email"
                  className="inline-flex items-center gap-2 text-pink-400 hover:text-pink-300 transition-colors"
                >
                  Configurer les notifications
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Démo Widget */}
      <section className="py-20 bg-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">
              Testez le <GradientText>Widget</GradientText>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Découvrez notre widget en action directement sur cette page.
            </p>
          </div>

          <div className="relative h-[600px] bg-gray-900/50 rounded-xl border border-white/10">
            <FloatingWidget publicId="demo" />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}